import {
  STREAMLAIN_MOTION,
  streamLainAnimationController,
} from '@blocksuite/affine-shared/utils';
import {
  Children,
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import * as styles from './index.css';

export const ANIMATED_PRESENCE_DURATION_MS = STREAMLAIN_MOTION.resize.duration;

type PresencePhase = 'entering' | 'present' | 'exiting';
type ChildItem = { child: ReactNode; key: string };
type PresenceEntry = ChildItem & { phase: PresencePhase };

function getChildren(children: ReactNode): ChildItem[] {
  return Children.toArray(children).map((child, index) => ({
    child,
    key: String((child as { key?: string | null }).key ?? index),
  }));
}

function reconcile(current: ChildItem[], previous: PresenceEntry[]) {
  const currentKeys = new Set(current.map(item => item.key));
  const previousByKey = new Map(previous.map(item => [item.key, item]));
  const next: PresenceEntry[] = current.map(item => {
    const before = previousByKey.get(item.key);
    return {
      ...item,
      phase: !before || before.phase === 'exiting' ? 'entering' : before.phase,
    };
  });
  previous.forEach((entry, index) => {
    if (!currentKeys.has(entry.key)) {
      next.splice(Math.min(index, next.length), 0, {
        ...entry,
        phase: 'exiting',
      });
    }
  });
  return next;
}

/** Each row owns one controller motion; removal follows actual completion. */
function PresenceItem({
  children,
  dataAttribute,
  phase,
  animate,
  animateEnteringHeight,
  onSettled,
  itemKey,
}: {
  children: ReactNode;
  dataAttribute?: string;
  phase: PresencePhase;
  animate: boolean;
  animateEnteringHeight: boolean;
  onSettled: (key: string, phase: PresencePhase) => void;
  itemKey: string;
}) {
  const elementRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const first = useRef(true);
  const opacity = useRef(phase === 'entering' ? 0 : 1);
  const dataAttributes = dataAttribute ? { [dataAttribute]: 'true' } : {};

  useLayoutEffect(() => {
    const element = elementRef.current;
    const content = contentRef.current;
    if (!element || !content) return;
    const initial = first.current;
    first.current = false;
    if (!animate || phase === 'present') {
      streamLainAnimationController.cancel(element);
      element.style.height = '';
      element.style.overflow = '';
      element.style.opacity = '1';
      opacity.current = 1;
      return;
    }
    const naturalHeight = content.getBoundingClientRect().height;
    const fromHeight =
      initial && phase === 'entering'
        ? 0
        : element.getBoundingClientRect().height;
    const toHeight = phase === 'exiting' ? 0 : naturalHeight;
    const fromOpacity = opacity.current;
    const toOpacity = phase === 'exiting' ? 0 : 1;
    const animateHeight = phase === 'exiting' || animateEnteringHeight;
    element.style.overflow = 'hidden';
    const update = (progress: number) => {
      if (animateHeight)
        element.style.height = `${fromHeight + (toHeight - fromHeight) * progress}px`;
      opacity.current = fromOpacity + (toOpacity - fromOpacity) * progress;
      element.style.opacity = String(opacity.current);
    };
    update(0);
    if (
      naturalHeight === 0 ||
      element.closest('[hidden], [aria-hidden="true"]')
    ) {
      update(1);
      onSettled(itemKey, phase);
      return;
    }
    const handle = streamLainAnimationController.animateProgress(
      element,
      update
    );
    void handle.finished.then(outcome => {
      if (outcome === 'finished') onSettled(itemKey, phase);
    });
    return () => handle.cancel();
  }, [animate, animateEnteringHeight, itemKey, onSettled, phase]);

  return (
    <div
      ref={elementRef}
      className={styles.presenceItem}
      data-presence={phase}
      inert={phase === 'exiting' ? true : undefined}
      data-static-enter-height={!animateEnteringHeight || undefined}
      {...dataAttributes}
    >
      <div className={styles.presenceContent} ref={contentRef}>
        {children}
      </div>
    </div>
  );
}

export function AnimatedPresenceList({
  children,
  itemDataAttribute,
  animate = true,
  animateEnteringHeight = true,
}: {
  children?: ReactNode;
  /** Kept for existing consumers; geometry comes from actual content. */
  defaultHeight?: number;
  itemDataAttribute?: string;
  /** External layout restoration settles all rows without entry/exit staging. */
  animate?: boolean;
  animateEnteringHeight?: boolean;
}) {
  const current = useMemo(() => getChildren(children), [children]);
  const [entries, setEntries] = useState<PresenceEntry[]>(() =>
    current.map(item => ({ ...item, phase: 'present' }))
  );
  const signature = current.map(item => item.key).join('\u001f');
  const currentRef = useRef(current);
  currentRef.current = current;
  const childrenByKey = useRef(new Map<string, ReactNode>());
  current.forEach(item => childrenByKey.current.set(item.key, item.child));

  useLayoutEffect(() => {
    setEntries(previous =>
      animate
        ? reconcile(currentRef.current, previous)
        : currentRef.current.map(item => ({
            ...item,
            phase: 'present' as const,
          }))
    );
  }, [animate, signature]);

  useLayoutEffect(() => {
    const retainedKeys = new Set(entries.map(entry => entry.key));
    currentRef.current.forEach(item => retainedKeys.add(item.key));
    for (const key of childrenByKey.current.keys()) {
      if (!retainedKeys.has(key)) childrenByKey.current.delete(key);
    }
  }, [entries]);

  const onSettled = useCallback((key: string, phase: PresencePhase) => {
    setEntries(previous =>
      previous.flatMap(entry => {
        if (entry.key !== key || entry.phase !== phase) return [entry];
        return phase === 'exiting'
          ? []
          : [{ ...entry, phase: 'present' as const }];
      })
    );
  }, []);

  return (
    <>
      {(animate
        ? entries
        : current.map(item => ({ ...item, phase: 'present' as const }))
      ).map(entry => (
        <PresenceItem
          key={entry.key}
          itemKey={entry.key}
          dataAttribute={itemDataAttribute}
          phase={entry.phase}
          animate={animate}
          animateEnteringHeight={animateEnteringHeight}
          onSettled={onSettled}
        >
          {childrenByKey.current.get(entry.key) ?? entry.child}
        </PresenceItem>
      ))}
    </>
  );
}
